import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import HRHeader from "../components/HRHeader";
import api, { downloadGuide } from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BookOpen, User, ShieldCheck, Download, ChevronRight, Search, Sparkles, CheckCheck } from "lucide-react";

const highlight = (text, q) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-inherit rounded-sm px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

const slugify = (s) => "guide-grp-" + (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const fmtUpd = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const GuidaPage = () => {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState("dipendente");
  const [downloading, setDownloading] = useState(false);
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [onlyNew, setOnlyNew] = useState(false);
  const [readSet, setReadSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("tb_guide_read") || "[]")); } catch { return new Set(); }
  });

  const markRead = (slug) => {
    setReadSet((prev) => {
      if (!slug || prev.has(slug)) return prev;
      const n = new Set(prev); n.add(slug);
      localStorage.setItem("tb_guide_read", JSON.stringify([...n]));
      return n;
    });
  };
  const isUnreadNew = (g) => g.new && !readSet.has(slugify(g.title));

  const markAllRead = () => {
    const slugs = (current?.groups || []).filter((g) => g.new).map((g) => slugify(g.title));
    setReadSet((prev) => {
      const n = new Set(prev);
      slugs.forEach((s) => n.add(s));
      localStorage.setItem("tb_guide_read", JSON.stringify([...n]));
      return n;
    });
  };

  useEffect(() => {
    api.get("/guide").then((r) => setSections(r.data?.sections || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const a = searchParams.get("a");
    if (a === "dipendente" || (a === "hr" && isAdmin)) { setActive(a); return; }
    if (isAdmin) setActive("hr");
  }, [isAdmin, searchParams]);

  // Guida contestuale: scrolla alla sezione richiesta via ?g=
  useEffect(() => {
    const g = searchParams.get("g");
    if (!g || !sections.length) return;
    const id = slugify(g);
    let tries = 0;
    const iv = setInterval(() => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); clearInterval(iv); }
      if (++tries > 20) clearInterval(iv);
    }, 100);
    return () => clearInterval(iv);
  }, [searchParams, sections, active]);

  const visible = sections.filter((s) => (isAdmin ? true : s.id === "dipendente"));
  const current = sections.find((s) => s.id === active) || visible[0];

  const handleDownload = async (audience) => {
    setDownloading(true);
    try {
      await downloadGuide(audience, audience === "all" ? "guida_portale.pdf" : `guida_${audience}.pdf`);
      toast.success("Guida scaricata in PDF");
    } catch { toast.error("Errore durante il download"); }
    finally { setDownloading(false); }
  };

  const ICON = { dipendente: User, hr: ShieldCheck };

  const q = query.trim().toLowerCase();
  const baseGroups = current?.groups || [];
  const newCount = baseGroups.filter((g) => isUnreadNew(g)).length;
  const hasNew = baseGroups.some((g) => g.new);
  const indexGroups = onlyNew ? baseGroups.filter((g) => g.new) : baseGroups;
  const filteredGroups = baseGroups
    .filter((g) => (onlyNew ? g.new : true))
    .map((g) => {
      const titleHit = g.title.toLowerCase().includes(q);
      const points = q ? g.points.filter((p) => p.toLowerCase().includes(q)) : g.points;
      if (!q) return g;
      if (titleHit) return g; // titolo corrisponde: mostra tutti i punti
      if (points.length) return { ...g, points };
      return null;
    })
    .filter(Boolean);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    markRead(id);
  };

  useEffect(() => {
    if (query.trim()) return; // niente scrollspy durante la ricerca
    const ids = indexGroups.map((g) => slugify(g.title));
    if (!ids.length) return;
    const onScroll = () => {
      let cur = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) cur = id;
      }
      setActiveId(cur);
      markRead(cur);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [current, query, onlyNew]);

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="guida-page">
      <HRHeader />
      <main className="max-w-[1180px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><BookOpen size={24} /> Guida al portale</h1>
            <p className="text-[14px] text-gray-500">Manuale operativo per l'utilizzo delle funzioni del portale HR del gruppo.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleDownload(active)} disabled={downloading} data-testid="guide-download-current" className="flex items-center gap-2 bg-white border border-[#16345c] text-[#16345c] hover:bg-[#16345c]/5 text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
              <Download size={16} /> Sezione in PDF
            </button>
            {isAdmin && (
              <button onClick={() => handleDownload("all")} disabled={downloading} data-testid="guide-download-all" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
                <Download size={16} /> Guida completa
              </button>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 mb-5" data-testid="guide-tabs">
            {visible.map((s) => {
              const Icon = ICON[s.id] || BookOpen;
              const on = active === s.id;
              return (
                <button key={s.id} onClick={() => setActive(s.id)} data-testid={`guide-tab-${s.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-[13px] font-bold transition-colors ${on ? "bg-[#16345c] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                  <Icon size={15} /> {s.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[420px]">
            <Search size={16} className="text-gray-400" />
            <input value={query} data-testid="guide-search" onChange={(e) => setQuery(e.target.value)} placeholder="Cerca un argomento nella guida..." className="flex-1 py-2.5 px-2 text-[14px] outline-none" />
            {query && <button onClick={() => setQuery("")} data-testid="guide-search-clear" className="text-[12px] text-gray-400 hover:text-gray-600">Azzera</button>}
          </div>
          {hasNew && (
            <button onClick={() => setOnlyNew((v) => !v)} data-testid="guide-only-new" aria-pressed={onlyNew}
              className={`flex items-center gap-1.5 text-[13px] font-bold px-4 py-2.5 rounded-sm border transition-colors ${onlyNew ? "bg-[#e30613] text-white border-[#e30613]" : "bg-white text-[#16345c] border-gray-300 hover:bg-gray-50"}`}>
              <Sparkles size={15} /> Solo novità
              <span data-testid="guide-new-count" className={`text-[11px] font-black px-1.5 py-0.5 rounded-full leading-none ${onlyNew ? "bg-white text-[#e30613]" : "bg-[#e30613] text-white"}`}>{newCount}</span>
            </button>
          )}
          {newCount > 0 && (
            <button onClick={markAllRead} data-testid="guide-mark-all-read" className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-600 hover:text-[#16345c] px-3 py-2.5 rounded-sm hover:bg-gray-100 transition-colors">
              <CheckCheck size={15} /> Segna tutto come letto
            </button>
          )}
        </div>

        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-6">
          {current && (
            <aside className="mb-5 lg:mb-0 lg:sticky lg:top-24 self-start" data-testid="guide-index">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2.5">Indice</div>
                <nav className="flex flex-col gap-1">
                  {indexGroups.map((g, gi) => {
                    const on = activeId === slugify(g.title);
                    return (
                      <button key={gi} onClick={() => scrollTo(slugify(g.title))} data-testid={`guide-index-${gi}`}
                        aria-current={on ? "true" : undefined}
                        className={`w-full text-left flex items-center gap-1.5 text-[12.5px] font-semibold rounded-sm px-3 py-2 border-l-2 transition-colors ${on ? "bg-[#16345c]/5 text-[#16345c] border-[#16345c]" : "text-gray-600 border-transparent hover:bg-gray-50 hover:text-[#16345c]"}`}>
                        <span className="flex-1">{g.title}</span>
                        {isUnreadNew(g) && <span className="text-[9px] font-bold uppercase bg-[#e30613] text-white px-1.5 py-0.5 rounded-full leading-none shrink-0">Nuovo</span>}
                      </button>
                    );
                  })}
                  {indexGroups.length === 0 && <p className="text-[12px] text-gray-400 px-1">Nessuna sezione.</p>}
                </nav>
              </div>
            </aside>
          )}

          {current && (
            <div className="space-y-4 min-w-0" data-testid="guide-content">
              {current.intro && !q && !onlyNew && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-[13.5px] text-[#16345c]">{current.intro}</div>
              )}
              {filteredGroups.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]" data-testid="guide-no-results">{q ? `Nessun risultato per "${query}".` : "Nessuna novità recente."}</div>
              ) : filteredGroups.map((g, gi) => (
                <div key={gi} id={slugify(g.title)} className="scroll-mt-24 bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2 flex-wrap">
                    <h2 className="text-[15px] font-bold text-[#16345c]">{highlight(g.title, q)}</h2>
                    {isUnreadNew(g) && <span data-testid={`guide-new-${gi}`} className="text-[9px] font-bold uppercase bg-[#e30613] text-white px-1.5 py-0.5 rounded-full leading-none">Nuovo</span>}
                    {g.updated && <span data-testid={`guide-updated-${gi}`} className="text-[11px] text-gray-400">Aggiornato il {fmtUpd(g.updated)}</span>}
                  </div>
                  <ul className="p-5 space-y-2.5">
                    {g.points.map((p, pi) => (
                      <li key={pi} className="flex items-start gap-2.5 text-[13.5px] text-gray-700 leading-relaxed">
                        <ChevronRight size={16} className="text-[#e30613] shrink-0 mt-0.5" />
                        <span>{highlight(p, q)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GuidaPage;
